import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronRight, FileSpreadsheet, Wallet, Users, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores/branchStore';
import { toast } from 'sonner';

const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_BADGE = {
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-100 text-amber-700 border-amber-200',
  unpaid: 'bg-rose-100 text-rose-700 border-rose-200',
  overpaid: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'n/a': 'bg-muted text-muted-foreground border-border',
};
const STATUS_LABEL = { paid: 'Paid', partial: 'Partial', unpaid: 'Unpaid', overpaid: 'Overpaid', 'n/a': '—' };
const defaultRange = () => {
  const now = new Date();
  const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const f = new Date(now.getFullYear() - 1, now.getMonth(), 1);
  const from = `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}`;
  return { from, to };
};

function balanceClass(v) {
  if (v > 0.01) return 'text-rose-600';      // owed to person
  if (v < -0.01) return 'text-amber-600';     // overpaid
  return 'text-emerald-600';
}

function ReportTable({ report, loading, entityLabel }) {
  const [open, setOpen] = useState({});
  if (loading) return <div className="p-8 text-sm text-muted-foreground" data-testid="report-loading">Loading…</div>;
  if (!report || report.groups.length === 0) return <div className="p-8 text-sm text-muted-foreground" data-testid="report-empty">No records for this period.</div>;
  return (
    <div className="space-y-2" data-testid="report-table">
      {report.groups.map((g) => {
        const isOpen = open[g.id];
        return (
          <div key={g.id} className="border rounded-lg overflow-hidden" data-testid={`report-row-${g.id}`}>
            <button onClick={() => setOpen(p => ({ ...p, [g.id]: !p[g.id] }))}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/40 text-left"
                    data-testid={`report-toggle-${g.id}`}>
              <div className="flex items-center gap-2 min-w-0">
                {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                <span className="font-medium truncate">{g.name}</span>
                <span className="text-[11px] text-muted-foreground">· {entityLabel === 'Partner' ? '' : g.branch_name + ' · '}SAR {fmt(g.monthly_salary)}/mo</span>
              </div>
              <div className="flex items-center gap-3 text-[12px] shrink-0">
                <span className="text-muted-foreground">Paid <strong className="text-foreground">SAR {fmt(g.totals.paid)}</strong></span>
                {g.totals.outstanding > 0.01 && <span className="text-rose-600">Owed SAR {fmt(g.totals.outstanding)}</span>}
                <span className={balanceClass(g.totals.running_balance)} title="Running balance (positive = owed to them, negative = overpaid)">
                  Bal SAR {fmt(g.totals.running_balance)}
                </span>
              </div>
            </button>
            {isOpen && (
              <div className="overflow-x-auto border-t bg-muted/10">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-1.5 font-medium">Month</th>
                      <th className="px-2 py-1.5 font-medium text-right">Monthly Salary</th>
                      <th className="px-2 py-1.5 font-medium text-center">Status</th>
                      <th className="px-2 py-1.5 font-medium">Payment Date</th>
                      <th className="px-2 py-1.5 font-medium">Method</th>
                      <th className="px-2 py-1.5 font-medium text-right">Paid Amount</th>
                      <th className="px-2 py-1.5 font-medium text-right">Outstanding</th>
                      <th className="px-2 py-1.5 font-medium text-right">Overpaid</th>
                      <th className="px-2 py-1.5 font-medium text-right">Underpaid</th>
                      <th className="px-3 py-1.5 font-medium text-right">Running Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.months.map((m) => (
                      <tr key={m.month} className="border-t border-border/50">
                        <td className="px-3 py-1.5">{m.month_label}</td>
                        <td className="px-2 py-1.5 text-right">{fmt(m.monthly_salary)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[m.status] || STATUS_BADGE['n/a']}`} data-testid={`status-${g.id}-${m.month}`}>
                            {STATUS_LABEL[m.status] || '—'}
                          </span>
                          {m.duplicate && <span className="ml-1 text-[10px] text-rose-600" title={`${m.payment_count} payments`}>×{m.payment_count}</span>}
                        </td>
                        <td className="px-2 py-1.5">{m.payment_date || '—'}</td>
                        <td className="px-2 py-1.5 capitalize">{m.payment_method || '—'}</td>
                        <td className="px-2 py-1.5 text-right">{fmt(m.paid_amount)}</td>
                        <td className="px-2 py-1.5 text-right text-rose-600">{m.outstanding ? fmt(m.outstanding) : '—'}</td>
                        <td className="px-2 py-1.5 text-right text-amber-600">{m.overpaid ? fmt(m.overpaid) : '—'}</td>
                        <td className="px-2 py-1.5 text-right text-orange-500">{m.underpaid ? fmt(m.underpaid) : '—'}</td>
                        <td className={`px-3 py-1.5 text-right font-medium ${balanceClass(m.running_balance)}`}>{fmt(m.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function SalaryReportPage() {
  const { branches, fetchBranches } = useBranchStore();
  const init = defaultRange();
  const [tab, setTab] = useState('employees');
  const [fromMonth, setFromMonth] = useState(init.from);
  const [toMonth, setToMonth] = useState(init.to);
  const [branchId, setBranchId] = useState('all');
  const [empReport, setEmpReport] = useState(null);
  const [partnerReport, setPartnerReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchBranches?.(); }, [fetchBranches]);

  const loadEmp = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/salary-report', { params: { from_month: fromMonth, to_month: toMonth, branch_id: branchId } });
      setEmpReport(data);
    } catch { toast.error('Failed to load salary report'); } finally { setLoading(false); }
  }, [fromMonth, toMonth, branchId]);

  const loadPartner = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/partner-payment-report', { params: { from_month: fromMonth, to_month: toMonth } });
      setPartnerReport(data);
    } catch { toast.error('Failed to load partner report'); } finally { setLoading(false); }
  }, [fromMonth, toMonth]);

  useEffect(() => { if (tab === 'employees') loadEmp(); else loadPartner(); }, [tab, loadEmp, loadPartner]);

  const exportExcel = async () => {
    try {
      toast.loading('Generating Excel…');
      const path = tab === 'employees' ? '/salary-report/export' : '/partner-payment-report/export';
      const params = tab === 'employees' ? { from_month: fromMonth, to_month: toMonth, branch_id: branchId } : { from_month: fromMonth, to_month: toMonth };
      const res = await api.get(path, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${tab}_payment_report_${fromMonth}_to_${toMonth}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss(); toast.success('Excel downloaded');
    } catch { toast.dismiss(); toast.error('Export failed'); }
  };

  const report = tab === 'employees' ? empReport : partnerReport;

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5 p-2 sm:p-4" data-testid="salary-report-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Wallet size={22} /> Salary Payment Report</h1>
            <p className="text-sm text-muted-foreground mt-1">Track monthly salary due vs paid — outstanding, over/underpaid and running balance per person.</p>
          </div>
          <Button onClick={exportExcel} variant="outline" className="rounded-full" data-testid="salary-report-export-btn">
            <FileSpreadsheet size={15} className="mr-1.5" /> Export Excel
          </Button>
        </div>

        <Card>
          <CardContent className="py-3 flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">From</Label><Input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className="h-9 w-40" data-testid="report-from-month" /></div>
            <div><Label className="text-xs">To</Label><Input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className="h-9 w-40" data-testid="report-to-month" /></div>
            <div>
              <Label className="text-xs">Quick year</Label>
              <div className="flex gap-1">
                {Array.from({ length: 3 }, (_, k) => new Date().getFullYear() - k).map(y => (
                  <Button key={y} type="button" size="sm" variant="outline" className="h-9 rounded-full text-xs"
                          onClick={() => { setFromMonth(`${y}-01`); setToMonth(`${y}-12`); }}
                          data-testid={`report-year-${y}`}>{y}</Button>
                ))}
              </div>
            </div>
            {tab === 'employees' && (
              <div>
                <Label className="text-xs">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger className="h-9 w-48" data-testid="report-branch-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button variant="secondary" className="h-9" onClick={() => tab === 'employees' ? loadEmp() : loadPartner()} data-testid="report-apply-btn">
              <RefreshCw size={14} className="mr-1.5" /> Apply
            </Button>
          </CardContent>
        </Card>

        {report?.totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-summary">
            <Card><CardContent className="py-3"><div className="text-[11px] text-muted-foreground">Total Paid</div><div className="text-lg font-bold">SAR {fmt(report.totals.paid)}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-[11px] text-muted-foreground">Outstanding</div><div className="text-lg font-bold text-rose-600">SAR {fmt(report.totals.outstanding)}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-[11px] text-muted-foreground">Overpaid</div><div className="text-lg font-bold text-amber-600">SAR {fmt(report.totals.overpaid)}</div></CardContent></Card>
            <Card><CardContent className="py-3"><div className="text-[11px] text-muted-foreground">{tab === 'employees' ? 'Employees' : 'Partners'}</div><div className="text-lg font-bold">{report.groups.length}</div></CardContent></Card>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="employees" data-testid="tab-employees"><Users size={14} className="mr-1.5" />Employees</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners"><Users size={14} className="mr-1.5" />Partners</TabsTrigger>
          </TabsList>
          <TabsContent value="employees" className="mt-3">
            <ReportTable report={empReport} loading={loading} entityLabel="Employee" />
          </TabsContent>
          <TabsContent value="partners" className="mt-3">
            <ReportTable report={partnerReport} loading={loading} entityLabel="Partner" />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
